import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { formatUZS } from '@/lib/format';
import { Zap } from 'lucide-react';

/**
 * Tez sotuv paneli — so'nggi 30 kunlik sotuvlardan eng ko'p sotilgan
 * mahsulotlarni bir bosishda savatchaga qo'shish uchun ko'rsatadi.
 * Sotuv ma'lumoti bo'lmasa eng yangi mahsulotlar ko'rsatiladi.
 */
export default function QuickProducts({
  products,
  onAdd,
  merchantId,
}: {
  products: any[];
  onAdd: (p: any) => void;
  merchantId: string | null;
}) {
  const { t } = useTranslation();
  const { data: topProductIds = [] } = useQuery({
    queryKey: ['pos-quick-products', merchantId],
    queryFn: async () => {
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const counts = new Map<number, number>();
      // Up to ~4 pages (default 15/page) — enough to rank the top sellers
      // without paging through a merchant's entire sales history.
      for (let page = 1; page <= 4; page += 1) {
        const { data, error } = await apiClient.GET('/sales', {
          params: { query: { date_from: cutoff, page } },
        });
        if (error) throw error;
        const sales = data?.data ?? [];
        for (const sale of sales) {
          for (const item of sale.items ?? []) {
            if (item.product_id == null) continue;
            counts.set(item.product_id, (counts.get(item.product_id) ?? 0) + Number(item.quantity ?? 0));
          }
        }
        if (page >= (data?.meta?.last_page ?? 1)) break;
      }
      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([id]) => id);
    },
    enabled: !!merchantId && products.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (products.length === 0) return null;

  const byId = new Map(products.map(p => [p.id, p]));
  const quick = topProductIds.length > 0
    ? topProductIds.map(id => byId.get(id)).filter(Boolean)
    : [...products]
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
        .slice(0, 6);

  if (quick.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-1.5 mb-2">
        <Zap size={13} className="text-warning" />
        <span className="text-xs font-medium text-muted-foreground">
          {t('pos_quick.title')}
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {quick.map((p: any) => {
          const salePrice = Number(p.sale_price) || 0;
          const price = salePrice > 0 ? salePrice : Number(p.price) || 0;
          return (
            <button
              key={p.id}
              onClick={() => onAdd(p)}
              className="shrink-0 w-40 text-left bg-card border border-border rounded-lg px-3 py-2 hover:border-primary/40 hover:shadow-sm transition-colors"
              title={p.name}
            >
              <p className="text-xs font-medium text-foreground line-clamp-1">{p.name}</p>
              <p className="text-xs text-primary font-semibold mt-0.5">{formatUZS(price)}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
